import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Download, ReceiptText } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { csvName, downloadCsv } from "../../../../lib/csv";
import { Avatar, Card, CardHead, EmptyState, IconBtn, PillTabs, StatusPill, fmtDate, fmtMoney, listCard } from "../../../ui";
import type { StatusTone } from "../../../ui";
import type { DashInvoice } from "../../../../lib/dashboard";

const COLS = "minmax(0,2.1fr) 1fr 1fr 1fr 1.2fr";

/** Paid is settled, overdue is late, everything else is still in flight. */
const TONE: Record<string, StatusTone> = {
  paid: "ok",
  overdue: "lt",
  partially_paid: "wt",
  sent: "wt",
  draft: "mut",
  cancelled: "mut",
};

/**
 * Invoices with money still to come.
 *
 * Not simply "everything that is not paid": a draft has not been sent to
 * anybody and a cancelled one never will be, so counting either as outstanding
 * would inflate the tab against every other figure on this screen.
 */
const AWAITING = new Set(["sent", "overdue", "partially_paid"]);

type Tab = "all" | "unpaid";

/**
 * The five newest invoices.
 *
 * The tab filters those five and does not fetch a different five - which is why
 * the subtitle and the empty state both name the scope out loud. "No unpaid
 * invoices" would be a claim about the whole ledger; "none of the five newest
 * is unpaid" is the claim this card can actually support, and it happens to be
 * the more useful sentence anyway.
 */
export function InvoicesCard({ invoices }: { invoices: DashInvoice[] }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>("all");

  const unpaid = invoices.filter((i) => AWAITING.has(i.status));
  const rows = tab === "unpaid" ? unpaid : invoices;

  const exportCsv = () =>
    downloadCsv(csvName("invoices", new Date()), [
      [
        t("dash.invoices.number"),
        t("dash.invoices.client"),
        t("dash.invoices.date"),
        t("dash.invoices.amount"),
        t("dash.invoices.status"),
      ],
      // The amount goes out in euros as a number, not as the formatted string
      // on screen: a spreadsheet cannot add "1 240,00 €".
      ...rows.map((i) => [i.number, i.clientName, i.issueDate.slice(0, 10), i.totalCents / 100, t(`sales.status.${i.status}`)]),
    ]);

  return (
    <Card>
      <CardHead
        title={t("dash.invoices.title")}
        sub={t(tab === "unpaid" ? "dash.invoices.subUnpaid" : "dash.invoices.sub", { n: invoices.length })}
        actions={
          <>
            <PillTabs
              small
              value={tab}
              onChange={setTab}
              tabs={[
                { value: "all" as Tab, label: t("dash.invoices.all") },
                { value: "unpaid" as Tab, label: t("dash.invoices.unpaid"), count: unpaid.length },
              ]}
            />
            <IconBtn
              onClick={exportCsv}
              disabled={rows.length === 0}
              title={t("dash.export")}
              aria-label={t("dash.export")}
            >
              <Download className="size-4" />
            </IconBtn>
          </>
        }
      />
      {rows.length === 0 ? (
        <EmptyState
          icon={ReceiptText}
          title={t(invoices.length === 0 ? "dash.invoices.emptyTitle" : "dash.invoices.allPaidTitle")}
          body={t(invoices.length === 0 ? "dash.invoices.emptyBody" : "dash.invoices.allPaidBody", {
            n: invoices.length,
          })}
        />
      ) : (
        <>
          <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 22 }}>
            <span>{t("dash.invoices.client")}</span>
            <span>{t("dash.invoices.number")}</span>
            <span>{t("dash.invoices.date")}</span>
            <span>{t("dash.invoices.amount")}</span>
            <span>{t("dash.invoices.status")}</span>
          </div>
          <div className="crm-rows pt-0" style={listCard(COLS)}>
            {rows.map((inv) => (
              <NavLink
                key={inv.id}
                to={`/sales/invoices/${inv.id}`}
                className="crm-list-row"
                style={{ gridTemplateColumns: COLS }}
              >
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar name={inv.clientName} />
                  <span className="min-w-0">
                    <span className="crm-prow-n crm-trunc block">{inv.clientName}</span>
                    <span className="crm-prow-s crm-trunc block">{inv.clientEmail ?? "-"}</span>
                  </span>
                </span>
                <span className="crm-mut crm-tnum text-[12.5px]">{inv.number}</span>
                <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(inv.issueDate)}</span>
                <span className="crm-tnum text-[13.25px] font-bold">{fmtMoney(inv.totalCents, true)}</span>
                <span>
                  <StatusPill tone={TONE[inv.status] ?? "mut"}>{t(`sales.status.${inv.status}`)}</StatusPill>
                </span>
              </NavLink>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
